/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Quotation API Service
 * CRUD + status transition endpoints for /api/v1/venta/quotations
 */

import apiClient from '@/services/api/client';
import { get, post, put, del } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type { PagedResult } from '../types/product';
import type {
  QuotationListItem,
  QuotationDetail,
  QuotationStats,
  CreateQuotationRequest,
} from '../types/quotation';

const BASE = '/venta/quotations';

export interface QuotationListParams {
  search?: string;
  status?: string;
  partnerId?: string;
  page?: number;
  size?: number;
  sort?: string;
  direction?: string;
}

export async function getQuotations(params: QuotationListParams = {}): Promise<PagedResult<QuotationListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.search) queryParams.search = params.search;
  if (params.status) queryParams.status = params.status;
  if (params.partnerId) queryParams.partnerId = params.partnerId;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<QuotationListItem>>>(BASE, queryParams);
  return response.data;
}

export async function getQuotation(id: string): Promise<QuotationDetail> {
  const response = await get<ApiResponse<QuotationDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function getQuotationStats(): Promise<QuotationStats> {
  const response = await get<ApiResponse<QuotationStats>>(`${BASE}/stats`);
  return response.data;
}

export async function createQuotation(data: CreateQuotationRequest): Promise<QuotationDetail> {
  const response = await post<ApiResponse<QuotationDetail>>(BASE, data);
  return response.data;
}

export async function updateQuotation(id: string, data: CreateQuotationRequest): Promise<QuotationDetail> {
  const response = await put<ApiResponse<QuotationDetail>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function deleteQuotation(id: string): Promise<void> {
  await del<ApiResponse<void>>(`${BASE}/${id}`);
}

export async function sendQuotation(id: string): Promise<QuotationDetail> {
  const response = await post<ApiResponse<QuotationDetail>>(`${BASE}/${id}/send`);
  return response.data;
}

export async function acceptQuotation(id: string): Promise<QuotationDetail> {
  const response = await post<ApiResponse<QuotationDetail>>(`${BASE}/${id}/accept`);
  return response.data;
}

export async function rejectQuotation(id: string): Promise<QuotationDetail> {
  const response = await post<ApiResponse<QuotationDetail>>(`${BASE}/${id}/reject`);
  return response.data;
}

export async function convertQuotationToOrder(id: string): Promise<QuotationDetail> {
  const response = await post<ApiResponse<QuotationDetail>>(`${BASE}/${id}/convert-to-order`);
  return response.data;
}

export async function duplicateQuotation(id: string): Promise<QuotationDetail> {
  const response = await post<ApiResponse<QuotationDetail>>(`${BASE}/${id}/duplicate`);
  return response.data;
}

/** Download quotation PDF as blob */
export async function downloadQuotationPdf(
  id: string,
  template?: string,
  lang: string = 'en'
): Promise<Blob> {
  const params: Record<string, string> = { lang };
  if (template) params.template = template;
  const response = await apiClient.get(`${BASE}/${id}/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
}

export const quotationService = {
  getQuotations,
  getQuotation,
  getQuotationStats,
  createQuotation,
  updateQuotation,
  deleteQuotation,
  sendQuotation,
  acceptQuotation,
  rejectQuotation,
  convertQuotationToOrder,
  duplicateQuotation,
  downloadQuotationPdf,
};
